import React from 'react';
import { motion } from 'framer-motion';
import { Bug, Biohazard, ArrowRight } from 'lucide-react';
import { Card } from '../Card';
import { cn } from '../../../utils/cn';
import type { PestCardProps } from './PestCard.types';

// ============================================================
// PestCard - Level 3 Domain Card
// Wraps Card (Level 1) with pest/disease-specific layout.
// Used in PlantOPedia pest & disease listing views.
// See: DESIGN_SYSTEM.md for architecture rules.
// ============================================================

const variantConfig = {
  pest: {
    borderColor: 'hover:border-red-500/30',
    iconBg: 'bg-red-50 dark:bg-red-900/20',
    iconColor: 'text-red-500 dark:text-red-400',
    labelBg: 'bg-gray-100 dark:bg-zinc-800',
    dotActiveColor: 'bg-red-500',
    footerColor: 'text-red-600',
    Icon: Bug,
  },
  disease: {
    borderColor: 'hover:border-orange-500/30',
    iconBg: 'bg-orange-50 dark:bg-orange-900/20',
    iconColor: 'text-orange-500 dark:text-orange-400',
    labelBg: 'bg-gray-100 dark:bg-zinc-800',
    dotActiveColor: 'bg-orange-500',
    footerColor: 'text-orange-600',
    Icon: Biohazard,
  },
};

export const PestCard: React.FC<PestCardProps> = ({
  pest,
  variant = 'pest',
  onClick,
  className,
}) => {
  const config = variantConfig[variant];
  const IconComponent = config.Icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className={cn('cursor-pointer', className)}
    >
      <Card
        variant="glass"
        padding="none"
        className={cn(
          'h-full transition-colors overflow-hidden group relative flex flex-col',
          config.borderColor,
        )}
      >
        <div className="p-5 flex flex-col h-full">
          {/* Header: Icon + Category Label */}
          <div className="flex items-start gap-3 mb-3">
            <div
              className={cn(
                'p-3 rounded-xl group-hover:scale-110 transition-transform',
                config.iconBg,
                config.iconColor,
              )}
            >
              <IconComponent size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={cn(
                  'text-xs px-2 py-1 rounded-full text-gray-500 font-medium uppercase tracking-wider',
                  config.labelBg,
                )}
              >
                {pest.category === 'insect' ? 'Hmyz' : pest.category === 'fungus' ? 'Plíseň' : pest.category === 'bacterium' ? 'Bakterie' : pest.category === 'virus' ? 'Virus' : pest.category}
              </span>
            </div>
          </div>

          {/* Name */}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-0.5">{pest.name_ui}</h3>
          {pest.latin_name && (
            <p className="text-xs text-gray-500 italic mb-2">{pest.latin_name}</p>
          )}

          {/* Visual Signs Preview */}
          {pest.identification?.visual_signs?.length > 0 && (
            <p className="text-sm text-gray-600 dark:text-zinc-300 line-clamp-2 mb-3">
              {pest.identification.visual_signs[0]}
            </p>
          )}

          {/* Severity */}
          <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-100 dark:border-zinc-800/50">
            <span className="text-xs text-gray-500">Závažnost:</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'w-2 h-2 rounded-full',
                    i <= pest.severity ? config.dotActiveColor : 'bg-gray-200 dark:bg-zinc-700',
                  )}
                />
              ))}
            </div>
          </div>

          {/* Footer */}
          {onClick && (
            <div
              className={cn(
                'flex items-center text-sm font-medium group-hover:translate-x-1 transition-transform mt-3',
                config.footerColor,
              )}
            >
              Detail <ArrowRight size={16} className="ml-1" />
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default PestCard;
